/**
 * One manual order taken offline, awaiting validation and submission.
 *
 * The largest of the three review cards, and the only one whose reviewer does
 * work rather than making a single choice: validating against live cloud rules,
 * acknowledging possible duplicates, and resolving an order-number collision.
 *
 * Moved out of ReconciliationView. See TCA-26081204.
 */
import React from 'react';
import { AlertTriangle, CheckCircle2, Loader2, RefreshCw, WifiOff } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { formatDeliveryDateDisplay } from '@/utils/dateUtils';
import { formatPhoneNumberDisplay } from '@/utils/phoneUtils';

const queueStateLabel = (state) => (
  state === 'submission_uncertain'
    ? 'Submission uncertain'
    : 'Pending review'
);

const QueueCard = ({
  item,
  validation,
  validating,
  submitting,
  discarding,
  numberConflict,
  manualNumber,
  duplicateAcknowledged,
  onDuplicateAcknowledgedChange,
  onManualNumberChange,
  onValidate,
  onSubmit,
  onDiscard,
}) => {
  const order = item.order;
  const outbox = item.outbox || {};
  const sync = order.desktop_sync || {};
  const normalized = validation?.normalized_order;
  const duplicateCount = validation?.duplicates?.length || 0;
  const requiresDuplicateAcknowledgment = duplicateCount > 0;
  const submitDisabled = (
    submitting
    || validating
    || !validation?.submit_ready
    || (requiresDuplicateAcknowledgment && !duplicateAcknowledged)
  );
  const submitLabel = (
    (outbox.state || sync.state) === 'submission_uncertain'
      ? 'Resolve Submission'
      : 'Submit as Cloud Order'
  );
  const workingNumber = order.internal_order_number
    || sync.provisional_internal_order_number
    || sync.temporary_label
    || `Offline #${sync.temporary_sequence || '?'}`;

  return (
    <Card
      className="border-orange-300 bg-orange-50"
      data-testid={`reconciliation-order-${order.id}`}
    >
      <CardHeader className="pb-3">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <CardTitle className="flex items-center gap-2 text-lg">
              <WifiOff className="h-4 w-4 text-orange-700" />
              {workingNumber}
              <span className="text-stone-900">{order.recipient_name}</span>
            </CardTitle>
            <CardDescription>
              {queueStateLabel(outbox.state || sync.state)}
              {order.delivery_date ? ` · ${formatDeliveryDateDisplay(order.delivery_date)}` : ''}
            </CardDescription>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onDiscard(item)}
              disabled={validating || submitting || discarding}
              className="text-red-700 hover:bg-red-50 hover:text-red-800"
              data-testid={`discard-local-order-${order.id}`}
            >
              {discarding ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Discarding...
                </>
              ) : 'Discard Offline Order'}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => onValidate(order)}
              disabled={validating || submitting || discarding}
              data-testid={`validate-local-order-${order.id}`}
            >
              {validating ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Validating...
                </>
              ) : (
                <>
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Validate
                </>
              )}
            </Button>
            <Button
              type="button"
              onClick={() => onSubmit(item)}
              disabled={submitDisabled || discarding}
              data-testid={`submit-local-order-${order.id}`}
            >
              {submitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Submitting...
                </>
              ) : submitLabel}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 gap-2 text-sm md:grid-cols-2">
          <div>
            <span className="font-medium text-stone-700">Address: </span>
            <span className="text-stone-900">
              {[order.recipient_street_address, order.recipient_city, order.recipient_state, order.recipient_zip_code].filter(Boolean).join(', ') || 'N/A'}
            </span>
          </div>
          <div>
            <span className="font-medium text-stone-700">Phone: </span>
            <span className="text-stone-900">{formatPhoneNumberDisplay(order.recipient_phone) || 'N/A'}</span>
          </div>
          <div>
            <span className="font-medium text-stone-700">Source: </span>
            <span className="text-stone-900">{order.network_source || 'N/A'}</span>
          </div>
          <div>
            <span className="font-medium text-stone-700">External #: </span>
            <span className="text-stone-900">{order.order_number || 'N/A'}</span>
          </div>
        </div>

        {numberConflict && (
          <div
            className="rounded-lg border border-amber-300 bg-white p-3 text-sm text-stone-700"
            data-testid={`number-conflict-${order.id}`}
          >
            <div className="mb-2 font-medium text-amber-900">
              Order number {numberConflict.number || workingNumber} is already used online.
            </div>
            <p className="mb-3">
              This likely means another order was created while this computer was offline.
              Choose a new number for this offline order, then submit again.
            </p>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <Button
                type="button"
                variant="outline"
                disabled={submitting || validating}
                onClick={() => onSubmit(item, { internalOrderNumber: null, retryMode: 'next' })}
                data-testid={`assign-next-number-${order.id}`}
              >
                Assign next available number
              </Button>
              <div className="flex items-center gap-2">
                <input
                  value={manualNumber || ''}
                  onChange={(event) => onManualNumberChange(order.id, event.target.value)}
                  placeholder="YYYYMMDD-NNN"
                  className="h-10 rounded-md border border-stone-200 bg-white px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-stone-400"
                  data-testid={`manual-number-input-${order.id}`}
                />
                <Button
                  type="button"
                  disabled={submitting || validating || !(manualNumber || '').trim()}
                  onClick={() => onSubmit(item, {
                    internalOrderNumber: (manualNumber || '').trim(),
                    retryMode: `manual-${(manualNumber || '').trim()}`,
                  })}
                  data-testid={`submit-manual-number-${order.id}`}
                >
                  Use this number
                </Button>
              </div>
            </div>
          </div>
        )}

        {validation && (
          <div
            className={`rounded-lg border p-3 text-sm ${
              validation.submit_ready
                ? 'border-emerald-200 bg-emerald-50'
                : 'border-amber-200 bg-amber-50'
            }`}
            data-testid={`validation-result-${order.id}`}
          >
            <div className="mb-2 flex items-center gap-2 font-medium">
              {validation.submit_ready ? (
                <CheckCircle2 className="h-4 w-4 text-emerald-700" />
              ) : (
                <AlertTriangle className="h-4 w-4 text-amber-700" />
              )}
              {validation.submit_ready ? 'Cloud validation passed' : 'Needs correction'}
            </div>
            <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
              <div>
                <span className="font-medium">Normalized address: </span>
                <span>
                  {normalized?.formatted_address
                    || [normalized?.recipient_street_address, normalized?.recipient_city, normalized?.recipient_state, normalized?.recipient_zip_code].filter(Boolean).join(', ')
                    || 'N/A'}
                </span>
              </div>
              <div>
                <span className="font-medium">Geocoding: </span>
                <span>{validation.geocoding?.status || 'N/A'}</span>
              </div>
              <div>
                <span className="font-medium">Source mapping: </span>
                <span>
                  {validation.source_resolution?.resolved
                    ? validation.source_resolution.source_name
                    : validation.source_resolution?.reason || 'unmapped'}
                </span>
              </div>
              <div>
                <span className="font-medium">Possible duplicates: </span>
                <span>{validation.duplicates?.length || 0}</span>
              </div>
            </div>
            {validation.warnings?.length > 0 && (
              <p className="mt-2 text-amber-800">
                Warnings: {validation.warnings.join(', ')}
              </p>
            )}
            {requiresDuplicateAcknowledgment && (
              <label className="mt-3 flex items-start gap-2 text-amber-900">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={duplicateAcknowledged}
                  onChange={(event) => onDuplicateAcknowledgedChange(
                    order.id,
                    event.target.checked,
                  )}
                  data-testid={`acknowledge-duplicates-${order.id}`}
                />
                <span>
                  I reviewed the possible duplicate order{duplicateCount !== 1 ? 's' : ''} and still want to submit this offline order.
                </span>
              </label>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default QueueCard;
